import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PhoenixTracker, TABS } from './pages/phoenix';
import { BottomTabBar } from './pages/phoenix/components/BottomTabBar';
import { PhasesPage } from './pages/phoenix/PhasesPage';
import { HealthProfilePage } from './pages/phoenix/HealthProfilePage';
import { FinancePage } from './pages/phoenix/FinancePage';
import { ReportsPage } from './pages/phoenix/ReportsPage';
import { JourneyPage } from './pages/phoenix/JourneyPage';
import { LearningPage } from './pages/phoenix/LearningPage';
import { EnglishPage } from './pages/phoenix/EnglishPage';
import { EnglishVocabPage } from './pages/phoenix/EnglishVocabPage';
import { EnglishReviewPage } from './pages/phoenix/EnglishReviewPage';
import { EnglishCoursePage } from './pages/phoenix/EnglishCoursePage';
import { EnglishTutorPage } from './pages/phoenix/EnglishTutorPage';
import { EnglishSpeakPage } from './pages/phoenix/EnglishSpeakPage';
import { ImmigrationPage } from './pages/phoenix/ImmigrationPage';
import { ImmigrationCountryPage } from './pages/phoenix/ImmigrationCountryPage';
import { ImmigrationStepPage } from './pages/phoenix/ImmigrationStepPage';
import { NewsPage } from './pages/phoenix/NewsPage';
import { WordlePage } from './pages/games/WordlePage';
import { QuestionsPage } from './pages/games/QuestionsPage';
import { QuizPage } from './pages/games/QuizPage';
import { QuizHistoryPage } from './pages/games/QuizHistoryPage';
import { SpyPage } from './pages/games/SpyPage';
import { SpyScoreboardPage } from './pages/games/SpyScoreboardPage';
import { SportsPage } from './pages/phoenix/SportsPage';
import { SportsMatchPage } from './pages/phoenix/SportsMatchPage';
import { SportsStandingsPage } from './pages/phoenix/SportsStandingsPage';
import { SportsTeamPage } from './pages/phoenix/SportsTeamPage';
import { NewsSourcePage } from './pages/phoenix/NewsSourcePage';
import { NewsHealthPage } from './pages/phoenix/NewsHealthPage';
import { NewsYouTubePage } from './pages/phoenix/NewsYouTubePage';
import { SettingsPage } from './pages/phoenix/SettingsPage';
import { ProfilePage } from './pages/phoenix/ProfilePage';
import { MessagesPage } from './pages/phoenix/MessagesPage';
import { MomentsPage } from './pages/phoenix/MomentsPage';
import { MomentsCapturePage } from './pages/phoenix/MomentsCapturePage';
import { MomentsDayPage } from './pages/phoenix/MomentsDayPage';
import { MomentsArchivePage } from './pages/phoenix/MomentsArchivePage';
import { MomentsDigestPage } from './pages/phoenix/MomentsDigestPage';
import { ReadingPage } from './pages/phoenix/ReadingPage';
import { ReadingNewPage } from './pages/phoenix/ReadingNewPage';
import { ReadingBookPage } from './pages/phoenix/ReadingBookPage';
import { ReadingRecapPage } from './pages/phoenix/ReadingRecapPage';
import { ReadingCatalogPage } from './pages/phoenix/ReadingCatalogPage';
import { ReadingReaderPage } from './pages/phoenix/ReadingReaderPage';
import { HabitsPage } from './pages/phoenix/HabitsPage';
import { PhysiqueCheckPage } from './pages/phoenix/PhysiqueCheckPage';
import { SleepPage } from './pages/phoenix/SleepPage';
import { GoalPacePage } from './pages/phoenix/GoalPacePage';
import { ConversationsPage } from './pages/phoenix/ConversationsPage';
import { FriendsPage } from './pages/phoenix/FriendsPage';
import { LifeSearchPage } from './pages/phoenix/LifeSearchPage';
import { PeoplePage } from './pages/phoenix/PeoplePage';
import { WishlistPage } from './pages/phoenix/WishlistPage';
import { LoginPage } from './pages/phoenix/LoginPage';
import { useAuth } from './contexts/AuthContext';
import { PageLoader } from './components/PageLoader';
import WorkoutShell from './pages/workout';
import { useTranslation } from 'react-i18next';
import PageTransition from './components/PageTransition';
import LoadingPage from './components/LoadingPage';
import InstallPrompt, { UpdateToast } from './components/InstallPrompt';
import NotificationPromptModal from './components/NotificationPromptModal';
import UnreadMessageGate from './components/UnreadMessageGate';
import { useNotificationPrompt } from './hooks/useNotificationPrompt';
import WelcomeCarousel from './components/WelcomeCarousel';
import SpotlightTour from './components/SpotlightTour';
import { TourAnchorProvider } from './contexts/TourAnchorContext';
import { useProfile } from './hooks/useProfile';
import DebugOverlay from './components/DebugOverlay';
import EnglishSelectionAction from './components/english/EnglishSelectionAction';
import OfflineBanner from './components/OfflineBanner';
import ConfirmDialog from './components/ConfirmDialog';
import { useIsMobile } from './hooks/useIsMobile';
import { useSwipeBack } from './hooks/useSwipeBack';
import { useAppFont } from './hooks/useAppFont';
import { usePushAutoRefresh } from './hooks/usePushAutoRefresh';
import ProfileSheet from './components/ProfileSheet';
import AssistantSheet from './components/AssistantSheet';
import MiniPlayer from './components/MiniPlayer';
import { MiniPlayerProvider } from './contexts/MiniPlayerContext';
import MobileBreadcrumb from './components/MobileBreadcrumb';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ConfirmDialogProvider } from './contexts/ConfirmDialogContext';
import { UnitsProvider } from './contexts/UnitsContext';
import { resolveEphemeralTab, resolveEphemeralPhoenixTab } from './lib/ephemeralTabs';
import { useBottomBarVisibility } from './hooks/useBottomBarVisibility';
import { cn } from './lib/utils';

// Shell layout. The multi-tenant sidebar (Hair / Skin / Nutrition picker)
// is gone — the app lives on its own domain now and the home route IS
// the Phoenix dashboard. What's left here:
//   • iOS-style edge-swipe-back gesture (self-gates)
//   • Mobile breadcrumb pill (self-gates to iOS standalone)
//   • Main content area
//   • Bottom tab bar on every authenticated route
//
// The bottom tab bar's auth gating used to be path-based ("only show
// when /phoenix or /workout") because there were other routes that
// shouldn't have it. With everything being Phoenix, the gate
// simplifies to "are we logged in?".
function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isRTL = i18n.language === 'ar';

  // iOS-style edge-swipe-back. Self-gates to standalone + mobile.
  useSwipeBack();

  // Self-heal stale push subscriptions on cold start. Apple silently
  // rotates APNs tokens on iOS PWA — without this hook the user
  // would stop receiving notifications and have no signal to act on.
  usePushAutoRefresh();

  // Set <html dir> + <html lang> on language change so the body picks
  // up the right RTL layout and the browser's auto-translate stops
  // re-translating Arabic content.
  React.useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language === 'ar' ? 'ar' : 'en';
  }, [i18n.language]);

  // Service-worker deeplink bridge. Notification taps post
  // { type: 'deeplink', url } and we translate that into an in-app
  // react-router navigation — no full reload, no blank screen.
  //
  // On cold start the React app mounts AFTER the SW's
  // notificationclick fires, so we also actively ask the SW for any
  // pending deeplink it stashed (within the last 30s). That covers
  // iOS PWA, where launching from a notification opens the PWA at
  // start_url and there's no other signal carrying the target URL.
  React.useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const onMessage = (e) => {
      const msg = e?.data;
      if (!msg || msg.type !== 'deeplink' || !msg.url) return;
      try {
        const u = new URL(msg.url, window.location.origin);
        if (u.origin !== window.location.origin) return;
        navigate(u.pathname + u.search + u.hash);
      } catch { /* ignore malformed */ }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    // Ask for a pending deeplink as soon as the SW is ready. We
    // intentionally use .ready (not .controller) because on a fresh
    // PWA install the controller can briefly be null while the SW
    // is still activating.
    navigator.serviceWorker.ready.then((reg) => {
      const sw = reg.active || navigator.serviceWorker.controller;
      try { sw && sw.postMessage({ type: 'check-pending-deeplink' }); }
      catch { /* SW gone */ }
    }).catch(() => { /* SW unavailable; fine */ });
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  // The chat thread owns the bottom edge with its own pinned composer,
  // so the floating tab bar there just stacks into a cluttered double
  // strip. Hide it on the thread (/messages/:partnerId) — the back
  // arrow + breadcrumb cover navigation. The conversations list
  // (/messages) is a plain page and keeps the bar.
  const isChatThread = /^\/messages\/[^/]+$/.test(location.pathname);
  // The book reader is a full-screen immersive surface (fixed inset-0)
  // — no bottom bar so the page is all reading area.
  const isBookReader = /^\/reading\/[^/]+\/read$/.test(location.pathname);
  const hasBottomTabBar = !!authUser && !isChatThread && !isBookReader;

  // First-launch notification permission prompt. Gated on auth so we
  // don't pop a modal on the login screen, and on the hook's internal
  // session/cool-down flags so it doesn't re-fire on every navigation.
  const notif = useNotificationPrompt();

  // Tour stack — welcome carousel → spotlight tour. Both fire only
  // when their respective user_profiles.*_at columns are null AND
  // the user is authenticated AND onboarding is complete. "Skip" on
  // either marks BOTH as seen so the user isn't re-prompted.
  const { profile, markWelcomeSeen, markTourSeen } = useProfile();
  const [tourActive, setTourActive] = React.useState(false);
  const onboarded = !!profile?.onboarded_at;
  const welcomeReady = !!authUser && onboarded && profile && !profile.welcome_seen_at;
  const tourReady = !!authUser && onboarded && profile && !!profile.welcome_seen_at && !profile.tour_seen_at;
  // When the spotlight tour eligibility flips on, activate it; the
  // carousel's "Get started" handler also sets this directly.
  React.useEffect(() => {
    if (tourReady) setTourActive(true);
  }, [tourReady]);
  const handleSkipWelcome = React.useCallback(async () => {
    await markWelcomeSeen();
    await markTourSeen();
    setTourActive(false);
  }, [markWelcomeSeen, markTourSeen]);
  const handleStartTour = React.useCallback(async () => {
    await markWelcomeSeen();
    setTourActive(true);
  }, [markWelcomeSeen]);
  const handleFinishTour = React.useCallback(async () => {
    setTourActive(false);
    await markTourSeen();
  }, [markTourSeen]);
  const handleSkipTour = handleFinishTour;

  return (
    <div className="flex h-screen bg-background relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <MobileBreadcrumb />
      <div className="flex-1 overflow-auto content-slide" data-shell-scroll>
        <div
          className={cn('min-h-full flex flex-col px-4 sm:px-6 lg:px-8 pb-0 content-fade lg:pt-8')}
          style={{
            // 4rem cleared the floating breadcrumb pill with ~24px to
            // spare on notched devices; 3.5rem still clears it cleanly
            // (pill bottom = safe-area + 0.75rem + 40px = safe-area +
            // 52px, page top = safe-area + 56px → 4px gap on notched,
            // tighter on non-notched but still no overlap) and reclaims
            // the slack short pages were wasting at the top.
            paddingTop: isMobile ? 'calc(env(safe-area-inset-top) + 3.5rem)' : undefined,
            paddingBottom: isMobile
              ? (hasBottomTabBar
                  ? 'calc(72px + env(safe-area-inset-bottom))'
                  : 'env(safe-area-inset-bottom)')
              : undefined,
          }}
        >
          <div className="flex-1">
            <OfflineBanner />
            <InstallPrompt />
            {authUser && notif.shouldShowModal && (
              <NotificationPromptModal
                onClose={notif.dismissModal}
                onPermissionChange={notif.updatePermission}
              />
            )}
            {welcomeReady && (
              <WelcomeCarousel onSkip={handleSkipWelcome} onStart={handleStartTour} />
            )}
            <SpotlightTour
              open={tourActive}
              onFinish={handleFinishTour}
              onSkip={handleSkipTour}
            />
            <DebugOverlay />
            <EnglishSelectionAction />
            <PageTransition>
              <Routes>
                <Route path="/" element={<PhoenixTracker />} />
                <Route path="/phases" element={<RequireAuth><PhasesPage /></RequireAuth>} />
                <Route path="/health" element={<RequireAuth><HealthProfilePage /></RequireAuth>} />
                <Route path="/finance" element={<RequireAuth><FinancePage /></RequireAuth>} />
                <Route path="/reports" element={<RequireAuth><ReportsPage /></RequireAuth>} />
                <Route path="/journey" element={<RequireAuth><JourneyPage /></RequireAuth>} />
                <Route path="/learning" element={<RequireAuth><LearningPage /></RequireAuth>} />
                <Route path="/english" element={<RequireAuth><EnglishPage /></RequireAuth>} />
                <Route path="/english/vocab" element={<RequireAuth><EnglishVocabPage /></RequireAuth>} />
                <Route path="/english/review" element={<RequireAuth><EnglishReviewPage /></RequireAuth>} />
                <Route path="/english/course" element={<RequireAuth><EnglishCoursePage /></RequireAuth>} />
                <Route path="/english/tutor" element={<RequireAuth><EnglishTutorPage /></RequireAuth>} />
                <Route path="/english/speak" element={<RequireAuth><EnglishSpeakPage /></RequireAuth>} />
                <Route path="/immigration" element={<RequireAuth><ImmigrationPage /></RequireAuth>} />
                <Route path="/immigration/:slug" element={<RequireAuth><ImmigrationCountryPage /></RequireAuth>} />
                <Route path="/immigration/:slug/step/:stepNum" element={<RequireAuth><ImmigrationStepPage /></RequireAuth>} />
                {/* News / Social / Tech are list-only routes now. The
                    article view lives at /article/:id (sibling) so the
                    list can unmount on tap and remount from cache on
                    back-nav without re-evaluating any URL-derived
                    filter state mid-session. */}
                <Route path="/news" element={<RequireAuth><NewsPage mode="news" /></RequireAuth>} />
                <Route path="/news/health" element={<RequireAuth><NewsHealthPage /></RequireAuth>} />
                <Route path="/news/source/:slug" element={<RequireAuth><NewsSourcePage /></RequireAuth>} />
                <Route path="/social" element={<RequireAuth><NewsPage mode="social" /></RequireAuth>} />
                <Route path="/tech" element={<RequireAuth><NewsPage mode="tech" /></RequireAuth>} />
                {/* Legacy article URL redirects + cold-load handler.
                    The article view is a state-driven modal inside
                    NewsPage now (no route teardown on open/close so
                    the list DOM survives, killing the image-decode
                    flash on back). All of these paths land the user
                    on /{from} with state.__openArticle set; NewsPage
                    picks that up in its initializer, opens the modal,
                    and rewrites the address bar to /article/:id?from=…
                    so the shareable URL is correct. */}
                <Route path="/news/:id" element={<RequireAuth><LegacyArticleRedirect from="news" /></RequireAuth>} />
                <Route path="/social/:id" element={<RequireAuth><LegacyArticleRedirect from="social" /></RequireAuth>} />
                <Route path="/tech/:id" element={<RequireAuth><LegacyArticleRedirect from="tech" /></RequireAuth>} />
                <Route path="/article/:id" element={<RequireAuth><ArticleColdLoadRedirect /></RequireAuth>} />
                {/* /games hub — owner + trusted-guest only. PR 1 ships
                    Wordle; future games (Connections, Crossword) hang
                    off the same /games prefix. */}
                <Route path="/games/wordle" element={<RequireAuth><WordlePage /></RequireAuth>} />
                <Route path="/games/questions" element={<RequireAuth><QuestionsPage /></RequireAuth>} />
                <Route path="/games/quiz" element={<RequireAuth><QuizPage /></RequireAuth>} />
                <Route path="/games/quiz/history" element={<RequireAuth><QuizHistoryPage /></RequireAuth>} />
                <Route path="/games/spy" element={<RequireAuth><SpyPage /></RequireAuth>} />
                <Route path="/games/spy/scoreboard" element={<RequireAuth><SpyScoreboardPage /></RequireAuth>} />
                <Route path="/youtube" element={<RequireAuth><NewsYouTubePage /></RequireAuth>} />
                <Route path="/sports" element={<RequireAuth><SportsPage /></RequireAuth>} />
                <Route path="/sports/standings/:leagueId" element={<RequireAuth><SportsStandingsPage /></RequireAuth>} />
                <Route path="/sports/teams/:teamId" element={<RequireAuth><SportsTeamPage /></RequireAuth>} />
                <Route path="/sports/:id" element={<RequireAuth><SportsMatchPage /></RequireAuth>} />
                <Route path="/settings" element={<RequireAuth><SettingsPage /></RequireAuth>} />
                <Route path="/profile" element={<RequireAuth><ProfilePage /></RequireAuth>} />
                <Route path="/habits" element={<RequireAuth><HabitsPage /></RequireAuth>} />
                <Route path="/physique" element={<RequireAuth><PhysiqueCheckPage /></RequireAuth>} />
                <Route path="/sleep" element={<RequireAuth><SleepPage /></RequireAuth>} />
                <Route path="/goal-pace" element={<RequireAuth><GoalPacePage /></RequireAuth>} />
                <Route path="/messages" element={<RequireAuth><ConversationsPage /></RequireAuth>} />
                <Route path="/messages/:partnerId" element={<RequireAuth><MessagesPage /></RequireAuth>} />
                <Route path="/friends" element={<RequireAuth><FriendsPage /></RequireAuth>} />
                <Route path="/search" element={<RequireAuth><LifeSearchPage /></RequireAuth>} />
                <Route path="/people" element={<RequireAuth><PeoplePage /></RequireAuth>} />
                <Route path="/wishlist" element={<RequireAuth><WishlistPage /></RequireAuth>} />
                <Route path="/moments" element={<RequireAuth><MomentsPage /></RequireAuth>} />
                <Route path="/moments/archive" element={<RequireAuth><MomentsArchivePage /></RequireAuth>} />
                <Route path="/moments/digest/:yearMonth" element={<RequireAuth><MomentsDigestPage /></RequireAuth>} />
                <Route path="/moments/capture/:promptId" element={<RequireAuth><MomentsCapturePage /></RequireAuth>} />
                <Route path="/moments/:promptId" element={<RequireAuth><MomentsDayPage /></RequireAuth>} />
                <Route path="/reading" element={<RequireAuth><ReadingPage /></RequireAuth>} />
                <Route path="/reading/new" element={<RequireAuth><ReadingNewPage /></RequireAuth>} />
                <Route path="/reading/catalog" element={<RequireAuth><ReadingCatalogPage /></RequireAuth>} />
                <Route path="/reading/recap/:yearQuarter" element={<RequireAuth><ReadingRecapPage /></RequireAuth>} />
                <Route path="/reading/:bookId/read" element={<RequireAuth><ReadingReaderPage /></RequireAuth>} />
                <Route path="/reading/:bookId" element={<RequireAuth><ReadingBookPage /></RequireAuth>} />
                <Route path="/workout/*" element={<WorkoutShell />} />
                {/* Legacy /phoenix/* bookmarks redirect to the root-level
                    equivalent so push-notification deep links and any
                    saved URLs still resolve. */}
                <Route path="/phoenix/*" element={<LegacyPhoenixRedirect />} />
              </Routes>
            </PageTransition>
          </div>
          {/* Per-page footer removed — credits + version + socials
              moved into ProfileSheet so short pages like /games/wordle
              and the AI chat don't have to scroll past them. */}
        </div>
      </div>
      <UpdateToast />
      <UnreadMessageGate />
      {isMobile && hasBottomTabBar && <HoistedBottomTabBar />}
    </div>
  );
}

// Strips the leading /phoenix from a stale URL and replaces the
// history entry — replace: true so the back button doesn't bounce
// the user back to the legacy URL.
function LegacyPhoenixRedirect() {
  const location = useLocation();
  const target = location.pathname.replace(/^\/phoenix/, '') || '/';
  return <Navigate to={target + location.search + location.hash} replace />;
}

// Old article URLs (/news/:id, /social/:id, /tech/:id) sent by push
// notifications or saved as bookmarks before the refactor resolve
// here and bounce to /article/:id?from=…, which the cold-load handler
// below then routes into NewsPage's modal flow.
function LegacyArticleRedirect({ from }) {
  const { id } = useParams();
  return <Navigate to={`/article/${id}?from=${from}`} replace />;
}

// /article/:id is no longer a real route — the article view is a
// modal inside NewsPage. When the user lands here directly (refresh
// on a shared link, push notification tap, cold app launch from a
// bookmark), bounce to the originating list with state.__openArticle
// set; NewsPage's initializer reads that, opens the modal, and
// rewrites the address bar back to /article/:id so the URL looks
// right and is still shareable.
function ArticleColdLoadRedirect() {
  const { id } = useParams();
  const location = useLocation();
  const rawFrom = new URLSearchParams(location.search).get('from');
  const from = rawFrom === 'social' || rawFrom === 'tech' || rawFrom === 'news' ? rawFrom : 'news';
  return <Navigate to={`/${from}`} replace state={{ __openArticle: id }} />;
}

// Bottom tab bar rendered once at the App level so it survives
// navigation between the Phoenix tabs and /workout (the two surfaces
// that share it). Active tab is derived from the URL for workout/news
// and from a 'phoenix:tab' window event for the Phoenix sub-tabs.
function HoistedBottomTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  // Mirror PhoenixTracker's default — Today is the new landing tab,
  // not Weight. The phoenix:tab event from the tracker still updates
  // this state on every active-tab change; the default matters
  // because the tracker's initial dispatch happens before this
  // component's listener is attached (the useEffect attaching the
  // listener fires AFTER the tracker's dispatch effect), so the very
  // first paint of the bottom bar reads this default.
  const [phoenixTab, setPhoenixTab] = React.useState('today');
  const [profileOpen, setProfileOpen] = React.useState(false);
  // Hides the bar when the keyboard is up or the user scrolls down,
  // re-shows on upward scroll / blur / approaching top of page.
  // Always-shown while the profile sheet is open so the user has a
  // clean dismissal anchor.
  const barVisibleSignal = useBottomBarVisibility();
  const barVisible = profileOpen || barVisibleSignal;

  React.useEffect(() => {
    const onPhoenixTab = (e) => {
      if (e?.detail) setPhoenixTab(e.detail);
    };
    window.addEventListener('phoenix:tab', onPhoenixTab);
    return () => window.removeEventListener('phoenix:tab', onPhoenixTab);
  }, []);

  // Pure-JS derivations — recomputed every render, cheap, depend only
  // on location + state. Declared BEFORE the useCallbacks that close
  // over them, and BEFORE the auth-loading conditional render, so the
  // hook count is invariant across renders.
  //
  // Previously these sat after an `if (authLoading || !user) return null`
  // guard, and the useCallbacks below it. When auth flipped loading→ready
  // the hook count jumped from 7 to 9 and React threw #310 ("Rendered
  // more hooks than during the previous render"). PR #340's added
  // useBottomBarVisibility was the straw that surfaced it on every boot.
  // /article/:id is a sibling route now — for bottom-bar purposes,
  // treat it as if the user is back on the originating feed (read
  // from ?from=…) so the right tab stays active / the right
  // ephemeral chip shows while they're reading.
  const articleFrom = location.pathname.startsWith('/article/')
    ? new URLSearchParams(location.search).get('from')
    : null;
  const effectivePath = articleFrom === 'social' || articleFrom === 'tech' || articleFrom === 'news'
    ? `/${articleFrom}`
    : location.pathname;
  const onWorkoutPath = effectivePath.startsWith('/workout');
  const onSocialPath = effectivePath.startsWith('/social');
  const onNewsPath = effectivePath.startsWith('/news');
  const ephemeralRoute = resolveEphemeralTab(effectivePath);
  const ephemeralSubTab = ephemeralRoute || onWorkoutPath || onSocialPath || onNewsPath
    ? null
    : resolveEphemeralPhoenixTab(phoenixTab);
  const ephemeral = ephemeralRoute || ephemeralSubTab;
  const tabs = ephemeral
    ? [
        ...TABS.slice(0, -1),
        { key: ephemeral.key, icon: ephemeral.icon, labelKey: ephemeral.labelKey },
        TABS[TABS.length - 1],
      ]
    : TABS;
  const active = ephemeral
    ? ephemeral.key
    : profileOpen ? 'profile'
    : (onSocialPath ? 'social' : (onWorkoutPath ? 'workouts' : phoenixTab));

  // Re-tap detection: are we tapping the tab that's already active?
  // If so, scroll the main content area + any per-page scroll
  // container to the top instead of re-navigating. Standard behaviour
  // across the bar — matches iOS / Instagram / Twitter convention.
  const scrollActiveSurfaceToTop = React.useCallback(() => {
    const main = document.querySelector('main[data-shell-scroll]')
      || document.querySelector('.flex-1.overflow-auto')
    if (main && typeof main.scrollTo === 'function') {
      main.scrollTo({ top: 0, behavior: 'smooth' })
    }
    window.dispatchEvent(new CustomEvent('news:scroll-to-top'))
    window.dispatchEvent(new CustomEvent('phoenix:scroll-top'))
  }, [])

  const handleSelect = React.useCallback(
    (key) => {
      if (ephemeral && key === ephemeral.key) {
        scrollActiveSurfaceToTop()
        return
      }
      if (key === 'profile') {
        setProfileOpen(true);
        return;
      }
      if (key === 'workouts') {
        if (location.pathname === '/workout') {
          scrollActiveSurfaceToTop()
          return
        }
        navigate('/workout');
        return;
      }
      if (key === 'social') {
        if (location.pathname.startsWith('/social')) {
          scrollActiveSurfaceToTop()
          return;
        }
        let last = null;
        try { last = sessionStorage.getItem('phoenix:social-last-url'); } catch { /* ignore */ }
        navigate(last && last.startsWith('/social') ? last : '/social');
        return;
      }
      if (location.pathname === '/' && phoenixTab === key) {
        scrollActiveSurfaceToTop()
        return
      }
      navigate('/', { state: { tab: key } });
    },
    [navigate, ephemeral, location.pathname, phoenixTab, scrollActiveSurfaceToTop]
  );

  // Conditional render happens AFTER every hook above has run. Hooks
  // count is now invariant across renders — adding/removing the JSX
  // never moves the hook list.
  if (authLoading || !user) return null;

  return (
    <>
      <BottomTabBar tabs={tabs} active={active} onSelect={handleSelect} visible={barVisible} />
      <ProfileSheet open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}

// Wait for auth, redirect to login if unauthenticated. Used by every
// route that requires a logged-in user — i.e. nearly everything
// except the root dashboard (which renders LoginPage itself when
// signed out).
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <LoginPage />;
  return children;
}

// Listener-only wrapper for the Phoenix Assistant sheet. Lives inside
// the router so it sees auth context but outside any specific route
// so it stays mounted across navigation. Anywhere else that wants to
// open the assistant fires `phoenix:open-assistant`.
function AssistantHost() {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('phoenix:open-assistant', onOpen);
    return () => window.removeEventListener('phoenix:open-assistant', onOpen);
  }, []);
  return <AssistantSheet open={open} onClose={() => setOpen(false)} />;
}

function App() {
  useAppFont();
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) return <LoadingPage />;

  return (
    <ThemeProvider>
      <AuthProvider>
        <UnitsProvider>
          <ConfirmDialogProvider>
            <Router
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <MiniPlayerProvider>
                <TourAnchorProvider>
                  <AppShell />
                  <AssistantHost />
                  <MiniPlayer />
                </TourAnchorProvider>
              </MiniPlayerProvider>
            </Router>
            <ConfirmDialog />
          </ConfirmDialogProvider>
        </UnitsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
